import { useEffect, useState, useRef } from 'react';
import { grey } from '@mui/material/colors';
import { Settings, Insights } from '@mui/icons-material';
import { Button, Container, IconButton, SwipeableDrawer, Box, Toolbar, Drawer, useTheme, Backdrop } from '@mui/material';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { useDispatch, useSelector } from "react-redux"
import { setSource, getData, loadNextImage, loadPrevImage, setTimestamp, setInitialFooterHeight, setSiderWidth } from "../../api/dataSlice"
import { DateTimePicker, LocalizationProvider, TimePicker, CalendarPickerSkeleton } from '@mui/lab';
import { useWindowDimensions } from "../hooks/useWindowDimensions"
import { useNavigateWithQueryParams } from "../hooks/useNavigateWithQueryParams"
import { useKeycloak } from "../Keycloak"
import { getAiParameterId, useGetDataByCameraQuery, useGetAiParameterEnabledQuery } from "../../api/graphqlApi";
import { loadDashboardData, isDashboardDataLoaded, getDashboardDetectionFilenames, databaseDateToFilename } from "../Utilities"
import { useTranslation } from 'react-i18next';
import { setArchiveActions, setMysiteActions } from '../chatbot/ChatManager'
import AccessFooter from '../access/AccessFooter'
import useAccordionAutoExpand from './useAccordionAutoExpand'
import DashboardFooter from '../dashboard/DashboardFooter'
import Calendar from '../controls/Calendar'
import TimeSelector from '../controls/TimeSelector'
import TimelapseAccordion from '../timelapse/TimelapseAccordion'
import DashboardAccordion from '../dashboard/DashboardAccordion'
import AccessAccordion from '../access/AccessAccordion'
import ReportAccordion from '../report/ReportAccordion'
import DiaryAccordion from '../diary/DiaryAccordion'
import DownloadAccordion from '../download/DownloadAccordion'
import DayjsAdapter from '@mui/lab/AdapterDayjs';
import i18next from 'i18next';
import dayjs from 'dayjs';

const settingsOverlaySx = {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none"
}

const FooterSettingsButton = ({ onClick, disabled, sx: sxOverride }) => (
    <IconButton
        sx={{ p: 1, pointerEvents: "all", ...sxOverride }}
        onClick={onClick}
        color="primary"
        disabled={disabled}
    >
        <Settings sx={{ fontSize: "1.66rem" }} />
    </IconButton>
)

export default function ArchiveControls({ initialSiderWidth, onOpenSideDrawer, setPaddingFooter, drawerBleeding, component, onMysiteViewChange, mysiteView, onDashboardStateChange, dashboardState, onFooterDateRangeChange, onOpenDashboardDetection }) {

    const { DEBUG, BASEURL, CAMERAS, ROUTES: { TIMELAPSE, ACCESS, MYSITE } } = window.conf
    const { t } = useTranslation()
    const { source, index, timestamp } = useSelector(getData);
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { data, isLoading: loading } = useGetDataByCameraQuery(null, { pollingInterval: 600000 })
    const { appbarHeight } = useWindowDimensions()
    const { hasRouteAccess } = useKeycloak()

    const detectionCamId = dashboardState?.selectedCamera ?? CAMERAS?.[0]?.id ?? 0 // camera whose detections the archive button checks/opens

    const [detectionDataReady, setDetectionDataReady] = useState(() => isDashboardDataLoaded(detectionCamId))
    const [canStepLeft, setCanStepLeft] = useState(false)
    const [canStepRight, setCanStepRight] = useState(false)
    const [footerOpen, setFooterOpen] = useState(false)
    const [footerHeight, setFooterHeight] = useState(48)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [reportCameraId, setReportCameraId] = useState(null)
    const [diaryCameraId, setDiaryCameraId] = useState(null)

    const siderRef = useRef()
    const chatbotExpandRef = useRef(false) // one-shot boolean: useAccordionAutoExpand consumes it to skip override
    const stateRef = useRef({}) // mirror for chatbot getters
    const dispatch = useDispatch()
    const minDate = dayjs(source?.[0]?.[0]?.[0])
    const maxDate = dayjs(source?.[0]?.[source[0].length - 1]?.[0])
    const theme = useTheme()
    const startYRef = useRef(0)
    const isPullingRef = useRef(false)
    const mysiteViewRef = useRef(mysiteView)

    mysiteViewRef.current = mysiteView
    stateRef.current = { timestamp, canStepLeft, canStepRight, minDate: source ? minDate : null, maxDate: source ? maxDate : null }

    const pathname = window.location.pathname
    const accordionOffset = 32 // 16px padding on each side
    const closedFooterHeight = footerOpen ? footerHeight + 189 : footerHeight
    const fullHeight = isPortrait ? "100vh" : `calc(100vh - ${appbarHeight}px)`
    const isArchive = component === 3
    const isMySite = component === 4
    const navigate = useNavigateWithQueryParams()
    const hasDashboard = !!MYSITE && hasRouteAccess(MYSITE)
    const { data: reportAiParameter } = useGetAiParameterEnabledQuery({ aiParameterId: getAiParameterId('AI_REPORT_ENABLED') }, { skip: !hasDashboard })
    const hasDetections = reportAiParameter?.enabled // AI detections enabled for the deployment -> the archive button is available

    // detection data lags the archive, so a recent archive image may have no detections yet — load it on the archive route to label/route the button accordingly
    const selectedCamDatum = source?.[0]?.[index]?.[1]?.[detectionCamId]?.datum
    const selectedFilename = selectedCamDatum ? databaseDateToFilename(selectedCamDatum) : null
    const detectionFilenames = detectionDataReady ? getDashboardDetectionFilenames(detectionCamId) : null // per-image presence: filenames with ≥1 detected object
    const imageHasDetections = !!(selectedFilename && detectionFilenames?.has(selectedFilename))

    useEffect(() => {
        if (!(isArchive && hasDashboard && hasDetections)) return
        if (isDashboardDataLoaded(detectionCamId)) { setDetectionDataReady(true); return }
        let cancelled = false
        setDetectionDataReady(false)
        loadDashboardData(`${BASEURL}cam${detectionCamId + 1}/rag_data/`, detectionCamId).then(() => { if (!cancelled) setDetectionDataReady(true) })
        return () => { cancelled = true }
    }, [isArchive, hasDashboard, hasDetections, detectionCamId, BASEURL])

    // #region chatbot

    useEffect(() => {
        if (isArchive) {
            setArchiveActions({
                setDate: (dateTimeStr) => dispatch(setTimestamp(dateTimeStr)),
                stepLeft: () => { if (stateRef.current.canStepLeft) dispatch(loadPrevImage()) },
                stepRight: () => { if (stateRef.current.canStepRight) dispatch(loadNextImage()) },
                getDate: () => stateRef.current.timestamp?.isValid?.() ? stateRef.current.timestamp.format('YYYY-MM-DD HH:mm') : null,
                getMinDate: () => stateRef.current.minDate?.isValid?.() ? stateRef.current.minDate.format('YYYY-MM-DD') : null,
                getMaxDate: () => stateRef.current.maxDate?.isValid?.() ? stateRef.current.maxDate.format('YYYY-MM-DD') : null,
                getCanStepLeft: () => !!stateRef.current.canStepLeft,
                getCanStepRight: () => !!stateRef.current.canStepRight,
            })
            return () => setArchiveActions(null)
        }
    // register once per route flip; dispatch is stable, getters read via ref — not in deps on purpose
    }, [isArchive]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMySite) {
            setMysiteActions({
                expandSection: (panel) => {
                    chatbotExpandRef.current = true
                    setExpanded(panel)
                    onMysiteViewChange(
                        panel === 'dashboard' ? 'dashboard' :
                        panel === 'timelapse' ? 'timelapse' :
                        panel === 'access' ? 'access' : null
                    )
                },
                // active subpage for chatbot's _getActiveCameraSlot — full-page subpages from mysiteView (via ref to skip its state-churn), accordion-only ones from `expanded`
                getCurrentSubpage: () => {
                    if (mysiteViewRef.current === 'dashboard') return 'dashboard'
                    if (mysiteViewRef.current === 'timelapse') return 'timelapse'
                    if (mysiteViewRef.current === 'access') return 'access'
                    if (expanded === 'diary' || expanded === 'report' || expanded === 'download') return expanded
                    return null
                }
            })
            return () => setMysiteActions(null)
        }
    // re-register on `expanded` change so getCurrentSubpage's closure stays fresh
    }, [isMySite, expanded]) // eslint-disable-line react-hooks/exhaustive-deps

    // #endregion chatbot

    // auto-expand feature flags come from Redux (published by each accordion on mount/query resolution)
    useAccordionAutoExpand({
        isMySite,
        pathname,
        timelapsePath: TIMELAPSE?.path,
        accessPath: ACCESS?.path,
        expanded,
        chatbotExpandRef,
        mysiteViewRef,
        setExpanded,
        onMysiteViewChange,
    })

    const onChangeExpanded = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
        if (mysiteView) onMysiteViewChange(null); // clear any active sub-view
    }

    const toggleSideDrawer = (open) => {
        setIsDrawerOpen(open);
        onOpenSideDrawer(open);

        if (open) {
            document.body.style.overflow = 'auto'; // keep background (content) scrollable
        }
    }

    const toggleBottomDrawer = (panel) => {
        if (panel) {
            setFooterHeight(20)
            // only expand a specific accordion when panel is a string; `true` opens the drawer without pre-expanding
            if (typeof panel === 'string') {
                setExpanded(panel)
            }
            setFooterOpen(true)
        } else {
            setTimeout(() => {
                setFooterOpen(false);
            }, theme.transitions.duration.leavingScreen);
        }
    };

    const updateTimestamp = (timestamp) => {
        dispatch(setTimestamp(timestamp));
    }


    const handleTouchStart = (e) => {
        startYRef.current = e.touches[0].clientY;
        isPullingRef.current = e.target.closest(".puller") !== null;
    }

    const handleTouchMove = (e) => {
        const deltaY = e.touches[0].clientY - startYRef.current;

        if (isPullingRef.current && deltaY > 30) {
            toggleBottomDrawer(false)
        }
    }

    // archive → dashboard: open the detection viewer for the selected archive moment when that day has detections, else just show the dashboard (mirror of the dialog's "to image archive")
    const goToDetections = (openViewer) => {
        if (!timestamp?.isValid?.() || !MYSITE) return
        onOpenDashboardDetection?.(timestamp.format('YYYY-MM-DD'), timestamp.format('YYYY-MM-DDTHH:mm:ss'), openViewer)
        navigate(MYSITE.path)
    }

    function stepLeft() {
        if (Number.isFinite(index) && canStepLeft) {
            dispatch(loadPrevImage());
        }
    }

    function stepRight() {
        if (Number.isFinite(index) && canStepRight) {
            dispatch(loadNextImage());
        }
    }

    function shouldDisableDate(date) {
        var dict = source?.[1]
        var hasDay = dict?.[date.$y]?.[date.$M]?.[date.$D]
        return hasDay ? false : true
    }

    function shouldDisableTime(time, type) {
        var dict = source?.[1]
        switch (type) {
            case "hours":
                var hasHour = dict?.[timestamp?.$y]?.[timestamp?.$M]?.[timestamp?.$D]?.[time]
                return hasHour ? false : true
            case "minutes":
                var hasMinute = dict?.[timestamp?.$y]?.[timestamp?.$M]?.[timestamp?.$D]?.[timestamp?.$H]?.[time]
                return hasMinute ? false : true
            default:
                return false
        }
    }

    useEffect(() => {
        setPaddingFooter(closedFooterHeight)
    }, [closedFooterHeight, setPaddingFooter])

    // reset the collapsed-drawer peek height whenever the drawer closes
    // (toggleBottomDrawer shrinks footerHeight to 20 while opening; without this reset the peek stays cut off after closing)
    useEffect(() => {
        if (!footerOpen) {
            setFooterHeight(48)
            dispatch(setInitialFooterHeight(48))
        }
    }, [footerOpen, dispatch])

    // close footer when switching routes (e.g. MySite → Archive)
    useEffect(() => {
        setFooterOpen(false)
    }, [component])

    // keep accordion expansion in sync with mysiteView
    useEffect(() => {
        if (!isMySite) return
        if (mysiteView === 'dashboard' && expanded !== 'dashboard') setExpanded('dashboard')
        else if (mysiteView === 'access' && expanded !== 'access') setExpanded('access')
        else if (mysiteView === 'timelapse' && !isMobile && expanded !== 'timelapse') setExpanded('timelapse')
    }, [isMySite, mysiteView]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (index) {
            updateTimestamp(source?.[0]?.[index]?.[0])
        }
    // only react to index changes — adding `source`/`updateTimestamp` would fire on source updates too
    // (those are handled by the `data`-effect below) and would cause redundant timestamp dispatches
    }, [index]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (data) {
            // setSource picks index from state.timestamp (default dayjs() → latest; chatbot target → nearest)
            dispatch(setSource(data))
            if (DEBUG) console.log("[ArchiveControls] source", data)
        }
    }, [data, dispatch, DEBUG])

    // enable/disable stepping left/right executed by TimeSelector
    useEffect(() => {
        if (source && Number.isFinite(index)) {
            var list = source[0]
            var incrementedIndex = index - 1
            var decrementedIndex = index + 1

            if (list?.[incrementedIndex]) {
                setCanStepLeft(true)
            } else {
                setCanStepLeft(false)
            }

            if (list?.[decrementedIndex]) {
                setCanStepRight(true)
            } else {
                setCanStepRight(false)
            }
        }
    }, [source, index])

    useEffect(() => {
        if (footerOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => document.body.style.overflow = "auto";
    }, [footerOpen])

    useEffect(() => {
        return () => {
            onOpenSideDrawer(false)
            setIsDrawerOpen(false)
        }
    // unmount cleanup only — intentionally runs once
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const current = siderRef.current;
        if (!current) return;

        const observer = new ResizeObserver(() => {
            const newWidth = current.clientWidth - accordionOffset; // current sider width without padding of accordion
            dispatch(setSiderWidth(newWidth));
        });

        observer.observe(current);
        return () => observer.disconnect();
    }, [dispatch])

    const controlsArchive = (
        <Box sx={{ ...(isArchive && { mt: 2, pr: 2, pl: isMobile ? 0 : 2, width: isMobile ? "100%" : undefined, boxSizing: 'border-box' }) }}>

            {/* timepicker only for landscape */}
            {isLandscape && ( 
                <Box sx={{ justifyContent: "center", display: "flex" }}>
                    <LocalizationProvider
                        dateAdapter={DayjsAdapter}
                        locale={i18next.language}
                    >
                        <TimePicker
                            open={pickerOpen}
                            onClose={() => setPickerOpen(false)}
                            value={timestamp}
                            onChange={updateTimestamp}
                            renderInput={({ inputRef }) =>
                                <TimeSelector
                                    stepLeft={stepLeft}
                                    timestamp={timestamp}
                                    stepRight={stepRight}
                                    loading={loading}
                                    canStepLeft={canStepLeft}
                                    canStepRight={canStepRight}
                                    setPickerOpen={setPickerOpen}
                                    inputRef={inputRef}
                                    pb={isLandscape ? 2 : 0}
                                />
                            }
                            cancelText={t("actions.cancel")}
                            shouldDisableTime={shouldDisableTime}
                            showToolbar={false}
                        />
                    </LocalizationProvider>
                </Box>
            )}

            <Calendar
                timestamp={timestamp}
                setTimestamp={updateTimestamp}
                shouldDisableDate={shouldDisableDate}
                loading={loading}
            />

            {/* jump to the dashboard for the selected moment; opens the detection viewer when the selected image has detections, else just shows the dashboard (detection data lags the archive) */}
            {hasDashboard && hasDetections && detectionDataReady && (
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Insights />}
                    onClick={() => goToDetections(imageHasDetections)}
                    disabled={loading || !timestamp?.isValid?.()}
                    sx={{ mt: 2, mb: 1, textTransform: 'none' }}
                >
                    {imageHasDetections ? t('pages.dashboard.show_detected_objects') : t('pages.dashboard.show_dashboard')}
                </Button>
            )}

        </Box>
    )

    const controlsMySite = (
        <Box sx={{ width: "100%" }}>
            <DownloadAccordion
                expanded={expanded}
                onChangeExpanded={onChangeExpanded}
                timestamp={timestamp}
                shouldDisableDate={shouldDisableDate}
                loading={loading}
            />
            <TimelapseAccordion
                expanded={expanded}
                setExpanded={setExpanded}
                mysiteView={mysiteView}
                onMysiteViewChange={onMysiteViewChange}
                setFooterOpen={setFooterOpen}
                timestamp={timestamp}
                shouldDisableDate={shouldDisableDate}
                loading={loading}
            />
            <DiaryAccordion
                expanded={expanded}
                onChangeExpanded={onChangeExpanded}
                isPortrait={isPortrait}
                loading={loading}
                timestamp={timestamp}
                setTimestamp={updateTimestamp}
                shouldDisableDate={shouldDisableDate}
                footerOpen={footerOpen}
                initialCameraId={diaryCameraId}
                onCameraIdChange={setDiaryCameraId}
            />
            <ReportAccordion
                expanded={expanded}
                onChangeExpanded={onChangeExpanded}
                isPortrait={isPortrait}
                isMySite={isMySite}
                initialCameraId={reportCameraId}
                onCameraIdChange={setReportCameraId}
            />
            <DashboardAccordion
                expanded={expanded}
                setExpanded={setExpanded}
                onMysiteViewChange={onMysiteViewChange}
                isPortrait={isPortrait}
                loading={loading}
                timestamp={timestamp}
                onDashboardStateChange={onDashboardStateChange}
                dashboardState={dashboardState}
            />
            <AccessAccordion
                expanded={expanded}
                setExpanded={setExpanded}
                onMysiteViewChange={onMysiteViewChange}
                isPortrait={isPortrait}
            />
        </Box>
    )

    // mobile footer portrait
    if (isMobile && isPortrait) {
        return (
            <Container sx={{ backgroundColor: "black", height: "100%" }}>

                {/* grays out background when footer open */}
                
                <Backdrop
                    open={footerOpen}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleBottomDrawer(false)
                    }}
                />

                <SwipeableDrawer
                    anchor="bottom"
                    open={footerOpen}
                    onClose={() => toggleBottomDrawer(false)}
                    onOpen={() => toggleBottomDrawer(
                        mysiteView === 'timelapse' ? 'timelapse' :
                        mysiteView === 'access' ? 'access' :
                        mysiteView === 'dashboard' ? 'dashboard' : true // no sub-view: open drawer without pre-expanding
                    )}
                    swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                    disableSwipeToOpen={true} // prevent opening by swiping because its opened by buttons
                    ModalProps={{
                        keepMounted: true,
                        closeAfterTransition: true,
                        disableRestoreFocus: true,
                        hideBackdrop: true
                    }}
                    PaperProps={{
                        sx: {
                            height: `calc(${footerHeight} - ${drawerBleeding}px)`,
                            overflow: 'visible'
                        },
                        onTouchStart: footerOpen ? handleTouchStart : undefined,
                        onTouchMove: footerOpen ? handleTouchMove : undefined
                    }}
                    variant={footerOpen ? "persistent" : "temporary"}
                >

                    <Box
                        sx={{
                            position: 'absolute',
                            top: -footerHeight,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            visibility: 'visible',
                            right: 0,
                            left: 0,
                            backgroundColor: "white",
                            boxShadow: isMySite && mysiteView === 'dashboard' && !footerOpen ? `0 -8px 0 0 #f5f6fa` : 'none' // background of corners behind puller of closed dashboard drawer/footer
                        }}
                    >

                        {/* puller component to pull out the drawer */}

                        {footerOpen ? (
                            <Box
                                className="puller"
                                sx={{
                                    width: "100%",
                                    height: 24,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 6,
                                        backgroundColor: grey[300],
                                        borderRadius: 3
                                    }}
                                />
                            </Box>

                        ) : isMySite && mysiteView === 'dashboard' ? (

                            <DashboardFooter
                                dashboardState={dashboardState}
                                onDateRangeChange={onFooterDateRangeChange}
                                onOpenDrawer={() => toggleBottomDrawer('dashboard')}
                                loading={loading}
                            />
                        ) : isMySite && mysiteView === 'timelapse' ? (

                            /* timelapse: settings icon only */
                            <Box sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                height: "48px"
                            }}>
                                <FooterSettingsButton
                                    onClick={() => toggleBottomDrawer("timelapse")}
                                    sx={{ mr: 1 }}
                                />
                            </Box>

                        ) : isMySite && mysiteView === 'access' ? (

                            /* access monitoring: date selector (day only) + settings icon */
                            <AccessFooter
                                pickerOpen={pickerOpen}
                                setPickerOpen={setPickerOpen}
                                onOpenDrawer={() => toggleBottomDrawer("access")}
                            />

                        ) : (

                            <Box sx={{
                                position: "relative",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center"
                            }}>

                                <LocalizationProvider
                                    dateAdapter={DayjsAdapter}
                                    locale={i18next.language}
                                >
                                    <DateTimePicker
                                        ampm={false}
                                        label={t("date.date_n_time")}
                                        inputFormat={(t("date.format.full_date_time"))}
                                        mask={t("date.mask.full_date_time")}
                                        renderInput={({ inputRef }) =>
                                            <TimeSelector
                                                stepLeft={stepLeft}
                                                timestamp={timestamp}
                                                stepRight={stepRight}
                                                showDate={true}
                                                canStepLeft={canStepLeft}
                                                canStepRight={canStepRight}
                                                loading={loading}
                                                setPickerOpen={setPickerOpen}
                                                inputRef={inputRef}
                                            />
                                        }
                                        value={timestamp}
                                        onChange={updateTimestamp}
                                        cancelText={t("actions.cancel")}
                                        toolbarTitle={t("date.select_datetime")}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        loading={loading}
                                        shouldDisableDate={shouldDisableDate}
                                        shouldDisableTime={shouldDisableTime}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                        showTodayButton
                                        todayText={t("date.today")}
                                        open={pickerOpen}
                                        onClose={() => setPickerOpen(false)}
                                    />
                                </LocalizationProvider>

                                {isMySite && (
                                    <Box sx={settingsOverlaySx}>
                                        <FooterSettingsButton
                                            onClick={() => toggleBottomDrawer(true)}
                                            disabled={loading}
                                        />
                                    </Box>
                                )}

                            </Box>
                        )}

                    </Box>

                    {/* content of the drawer below puller component */}

                    {footerOpen && (
                        <Box
                            sx={{
                                maxHeight: `calc(${fullHeight} - ${drawerBleeding}px)`,
                                width: 'auto',
                                overflowX: 'hidden',
                                mt: "4px"
                            }}
                        >
                            {isMySite ? controlsMySite : controlsArchive}
                        </Box>
                    )}

                </SwipeableDrawer>
            </Container>
        )

    // mobile sider landscape
    } else if (isMobile && isLandscape) {
        return (
            <SwipeableDrawer
                key={initialSiderWidth} // avoid hiding bleed by forcing remount
                anchor="right"
                open={isDrawerOpen}
                onClose={() => toggleSideDrawer(false)}
                onOpen={() => toggleSideDrawer(true)}
                swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                disableSwipeToOpen={false}
                hideBackdrop={true} // prevent background from graying out outside the sider
                sx={{ pointerEvents: "none" }} // disable invisible layer that prevents interaction with the content (= background of the drawer)
                ModalProps={{
                    keepMounted: true,
                    closeAfterTransition: true,
                    disableRestoreFocus: true
                }}
                PaperProps={{
                    sx: {
                        width: initialSiderWidth - drawerBleeding,
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        height: `100%`,
                        pointerEvents: "auto" // necessary to remain interaction with the drawer
                    }
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        position: 'absolute',
                        height: '100%',
                        top: 0,
                        right: 0,
                        left: -drawerBleeding,
                        visibility: 'visible',
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >

                    {/* puller component to pull out the drawer */}

                    <Box
                        sx={{
                            position: 'absolute',
                            backgroundColor: isDrawerOpen ? '#e0e0e0' : '#003366',
                            height: 30,
                            width: 6,
                            top: `calc(50% + 8px)`,
                            left: 8,
                            borderRadius: 3
                        }}
                    />

                    {/* content of the drawer below puller component */}

                    <Box
                        sx={{
                            overflow: 'auto',
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mt: `${appbarHeight}px`,
                            ml: `${drawerBleeding}px`,
                            height: "100%",
                            maxHeight: fullHeight,
                            width: `calc(100% - ${drawerBleeding}px)`
                        }}
                    >
                        {isMySite ? controlsMySite : controlsArchive}
                    </Box>
                </Box>
            </SwipeableDrawer>
        )

    // desktop sider
    } else {
        return (
            <Drawer
                variant="permanent"
                anchor="right"
                PaperProps={{
                    sx: {
                        width: initialSiderWidth,
                        boxSizing: 'border-box'
                    }
                }}
            >
                <Toolbar /> {/* provides top distance without additional functionality */}

                <Box
                    ref={siderRef}
                    sx={{
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >
                    {isMySite ? controlsMySite : controlsArchive}
                </Box>
            </Drawer>
        )
    }
}